import { forwardRef, useImperativeHandle } from "react";
import { useFormContext } from "react-hook-form";
import { IdNameList } from "../../../../../api/services/common/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../components";
import { CurrencyFormat } from "../../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../../components/textFieldCurrency/TextFieldCurrency";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  gridSizeLongLabel,
  MAX_LENGTH_100,
  MAX_LENGTH_1000,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { CreateClientFormKey as ProgramTemplateFormKey } from "../../../modelsAndSchemas/Models";
import { billingAndPurchaseOrderKeys } from "../../../siteCore/SiteCoreKeys";
import { pageId } from "../../clientBillingPurchaseOrderMain/ClientBillingPurchaseOrderMain";
import { ConfirmationChangeState } from "../BillingProgramTemplate";
import { BillingTypeFieldWrapper } from "./BillingTypeField.style";
import { useBillingTypeFieldHook } from "./useBillingTypeFieldHook";

export interface BillingTypeFieldProps {
  formIdPrefix: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  defaultUserCurrency: string;
  currenciesList: IdNameList[];
  forceUpdate: () => void;
  setShowWarningOnProgramBillingChange: (
    confirmationState: ConfirmationChangeState,
  ) => void;
  templateIndex: number;
  setIsErrorFromProgramTemplate: (isErrorFromProgramTemplate: boolean) => void;
  isErrorFromProgramTemplate: boolean;
  lobId?: string;
}
export const BillingTypeField = forwardRef(
  (props: BillingTypeFieldProps, ref) => {
    const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
    const { control, setValue } = useFormContext();
    const {
      commonLabels,
      commonValidationMessages,
      fetchDropdownValues,
      fetchLabel,
      fetchValidationMessage,
    } = useSiteCore();
    const { commonARIALabel } = useNavigationSiteCore();

    const { isMobile } = useMediaQueries();

    const {
      billingDetailsAndPOTitle,
      addProgramTemplateTitle,
      billingTrigger,
      currency,
      programFee,
      billingTriggerList,
      billingTypeList,
      programTemplate,
      alternateDisplayName,
      billingType: billingTypeSiteCore,
      specialBillingInstructions,
    } = billingAndPurchaseOrderKeys;

    const {
      formIdPrefix,
      defaultUserCurrency,
      currencyFormat,
      currencyId,
      currenciesList,
      programTemplateDropdownData,
      handleProgramTemplateOrBillingTypeChange,
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
      programTemplateIdKey,
      billingTypeKey,
      feeWithFormatKey,
      feeKey,
      billingType,
      forceUpdate,
    } = useBillingTypeFieldHook(props);

    useImperativeHandle(ref, () => ({
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
    }));

    const commonTopSectionFields = (
      <>
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={programTemplateIdKey}
            key={programTemplateIdKey}
          >
            <MuiSelect
              data-testId={`${pageId}-${programTemplateIdKey}`}
              label={fetchLabel(billingDetailsAndPOTitle, programTemplate)}
              options={programTemplateDropdownData}
              defaultValue={""}
              visibleDefaultSelectOption={true}
              isMandatory
              showSearch
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => {
                handleProgramTemplateOrBillingTypeChange(e, true);
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={`${formIdPrefix}.${ProgramTemplateFormKey.alternateDisplayName}`}
          >
            <MuiTextField
              id={`${formIdPrefix}.${ProgramTemplateFormKey.alternateDisplayName}`}
              label={fetchLabel(billingDetailsAndPOTitle, alternateDisplayName)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field">
          <FormController control={control} name={billingTypeKey}>
            <MuiSelect
              id={`${pageId}-${billingTypeKey}`}
              label={fetchLabel(billingDetailsAndPOTitle, billingTypeSiteCore)}
              options={fetchDropdownValues(
                addProgramTemplateTitle,
                billingTypeList,
              )}
              defaultValue={""}
              visibleDefaultSelectOption={true}
              isMandatory
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) => {
                handleProgramTemplateOrBillingTypeChange(e, false);
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );

    const commonFields = (
      <>
        <MuiGrid size={gridSize} className="form-field-override">
          <FormController
            control={control}
            name={`${formIdPrefix}.${ProgramTemplateFormKey.currencyId}`}
          >
            <MuiSelect
              data-testId={`${pageId}-${ProgramTemplateFormKey.currencyId}`}
              label={fetchLabel(billingDetailsAndPOTitle, currency)}
              options={setApiDataToDropDownOption(currenciesList)}
              defaultValue={defaultUserCurrency}
              onChange={forceUpdate}
              visibleDefaultSelectOption={false}
              isMandatory
              showSearch
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className="form-field-override">
          <FormController control={control} name={feeWithFormatKey}>
            <TextFieldCurrency
              isMandatory
              id={`${pageId}-${formIdPrefix}.${ProgramTemplateFormKey.feeWithFormat}`}
              formId={feeWithFormatKey}
              locale={currencyFormat?.locale}
              isCurrencyChanged={currencyId}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={feeWithFormatKey}
              label={fetchLabel(addProgramTemplateTitle, programFee)}
              placeholder={commonLabels.inputPlaceHolder}
              getFinalValues={(value, valueNonFormatted) => {
                setValue(feeKey, valueNonFormatted, { shouldTouch: true });
                setValue(feeWithFormatKey, value, { shouldTouch: true });
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );

    return (
      <BillingTypeFieldWrapper>
        <MuiGrid container spacing={isMobile ? 0 : 2}>
          {commonTopSectionFields}

          {billingType ===
            BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity && (
            <MuiGrid size={gridSize} className="form-field-override">
              <FormController
                control={control}
                name={`${formIdPrefix}.${ProgramTemplateFormKey.billingTriggerId}`}
              >
                <MuiSelect
                  data-testId={`${pageId}-${ProgramTemplateFormKey.billingTriggerId}`}
                  label={fetchLabel(billingDetailsAndPOTitle, billingTrigger)}
                  options={fetchDropdownValues(
                    addProgramTemplateTitle,
                    billingTriggerList,
                  )}
                  defaultValue={""}
                  visibleDefaultSelectOption={true}
                  isMandatory
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          )}
          {commonFields}
          <MuiGrid container className="special-billing-instruction">
            <MuiGrid className="form-field" size={gridSizeLongLabel}>
              <FormController
                control={control}
                name={`${formIdPrefix}.${ProgramTemplateFormKey.specialBillingInstructions}`}
              >
                <MuiTextField
                  id={`${pageId}-${ProgramTemplateFormKey.specialBillingInstructions}`}
                  label={fetchLabel(
                    billingDetailsAndPOTitle,
                    specialBillingInstructions,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  className="notes-input"
                  multiline
                  maxLength={MAX_LENGTH_1000}
                  rows={4}
                  data-testId={`${pageId}-${ProgramTemplateFormKey.specialBillingInstructions}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </BillingTypeFieldWrapper>
    );
  },
);
